import type { ComponentProps, Ref } from 'react';
import { forwardRef } from 'react';
import { ScrollView, type ScrollViewInstance, StyleSheet, type View } from 'react-native';

import { theme } from '@garnish/constants';
import { useResponsive, VStack } from '@garnish/core';

import type { Customer } from '@sg/graphql-schema';

import { type useAddressAutocomplete } from '../../../../../../../hooks';
import { type MinimumAddress } from '../../../../../../../locations.types';
import { AddressSearchResult } from '../AddressSearchResult';
import { ErrorMessage } from '../ErrorMessage';
import { LoadingResults } from '../LoadingResults';
import { NoResults } from '../NoResults';

export const AddressSearchResultList = forwardRef(
  (props: AddressSearchResultsProps, ref: Ref<ScrollViewInstance>) => {
    const { match } = useResponsive();
    const { customerAddresses, predictionsData, skipLoading, style, onPress } = props;
    const { loading, error, noResults, predictions } = predictionsData;

    const containerStyles =
      style ??
      match([styles.addressSearchResultsContainerXs, styles.addressSearchResultsContainerSm]);

    return (
      <ScrollView testID="location.auto-complete-results" ref={ref} style={containerStyles}>
        {loading && !skipLoading ? (
          <LoadingResults />
        ) : noResults ? (
          <NoResults />
        ) : (
          <VStack gap={0} hasDivider>
            {predictions.map((location) => (
              <AddressSearchResult
                key={location.formattedAddress}
                addressPrediction={location}
                customerAddresses={customerAddresses}
                onPress={onPress}
              />
            ))}
          </VStack>
        )}

        {error ? <ErrorMessage minimalStyles /> : null}
      </ScrollView>
    );
  },
);

// ──── Types ──────────────────────────────────────────────────

type AddressSearchResultsProps = Readonly<{
  style?: ComponentProps<typeof View>['style'];
  skipLoading?: boolean;
  customerAddresses?: Customer['addresses'];
  predictionsData: Pick<
    ReturnType<typeof useAddressAutocomplete>,
    'noResults' | 'error' | 'loading' | 'predictions'
  >;
  onPress: (selectedAddress: MinimumAddress) => void;
}>;

// ──── Styles ───────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  addressSearchResultsContainerXs: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.fixed + 1, // cover up components that are below
    padding: theme.spacing['4'],
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  addressSearchResultsContainerSm: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
  },
});
